import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, DollarSign, Star, Truck, MapPin, Phone, Mail, Clock, Cog, Award, Heart, Zap, Target, Users, ArrowRight, Quote, Gauge } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { inventoryAPI, cmsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import { ContactPopup } from '@/components/ContactPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";
const ICONS = { Shield, DollarSign, Star, Truck, MapPin, Phone, Mail, Clock, Cog, Award, Heart, Zap, Target, Users };
const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : 'Call';
const formatKM = (km) => km ? `${Number(km).toLocaleString()} km` : '';

function PublicHero({ props, navigate }) {
  const overlayOpacity = props.overlayOpacity !== undefined ? props.overlayOpacity : 70;
  const showHeading = props.showHeading !== false;
  const showSubheading = props.showSubheading !== false;
  const showCtaPrimary = props.showCtaPrimary !== false;
  const showCtaSecondary = props.showCtaSecondary !== false;
  const hasButtons = (showCtaPrimary && props.ctaText) || (showCtaSecondary && props.ctaSecondaryText);

  // Viewport detection
  const detectViewport = () => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w < 768) return 'mobile';
    if (w < 1024) return h > w ? 'mobile' : 'tablet';
    return 'desktop';
  };
  const [viewport, setViewport] = useState(detectViewport);
  // Auto-fit: track the loaded image's natural ratio so the section height matches.
  const [imgRatio, setImgRatio] = useState(null); // width / height
  const sectionRef = useRef(null);
  const [sectionWidth, setSectionWidth] = useState(typeof window === 'undefined' ? 1440 : window.innerWidth);

  useEffect(() => {
    const update = () => {
      setViewport(detectViewport());
      if (sectionRef.current) setSectionWidth(sectionRef.current.offsetWidth);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // Image fallback chain
  const desktopImg = props.backgroundImage || '';
  const tabletImg = props.backgroundImageTablet || desktopImg;
  const mobileImg = props.backgroundImageMobile || tabletImg;
  const currentImg = viewport === 'mobile' ? mobileImg : viewport === 'tablet' ? tabletImg : desktopImg;

  // Reset ratio when image changes so we re-measure
  useEffect(() => { setImgRatio(null); }, [currentImg]);

  // Sensible caps so a very tall image can't make the hero gigantic.
  const minHeight = viewport === 'mobile' ? 220 : viewport === 'tablet' ? 280 : 320;
  const maxHeight = viewport === 'mobile' ? 520 : viewport === 'tablet' ? 520 : 600;

  // Display mode — admin choice from CMS: 'auto' (default), 'cover', or 'contain'.
  const displayMode = props.displayMode || 'auto';

  // Admin-set heights override defaults when provided (with sane bounds).
  const adminHDesktop = Number(props.heightDesktop) || 0;
  const adminHTablet = Number(props.heightTablet) || 0;
  const adminHMobile = Number(props.heightMobile) || 0;
  const adminHeight = viewport === 'mobile' ? adminHMobile : viewport === 'tablet' ? adminHTablet : adminHDesktop;
  const clampedAdminHeight = adminHeight > 0
    ? Math.min(viewport === 'mobile' ? 800 : viewport === 'tablet' ? 800 : 900,
               Math.max(240, adminHeight))
    : 0;

  let computedHeight;
  let objectFit;
  if (displayMode === 'cover') {
    computedHeight = clampedAdminHeight || (viewport === 'mobile' ? 320 : viewport === 'tablet' ? 380 : 460);
    objectFit = 'cover';
  } else if (displayMode === 'contain') {
    computedHeight = clampedAdminHeight || (viewport === 'mobile' ? 320 : viewport === 'tablet' ? 380 : 460);
    objectFit = 'contain';
  } else if (clampedAdminHeight) {
    // Auto mode but admin specified a height — respect it.
    computedHeight = clampedAdminHeight;
    if (imgRatio) {
      const fillHeight = sectionWidth / imgRatio;
      objectFit = Math.abs(fillHeight - clampedAdminHeight) < 20 ? 'cover' : 'contain';
    } else {
      objectFit = 'cover';
    }
  } else if (imgRatio) {
    const fillHeight = sectionWidth / imgRatio;
    if (fillHeight >= minHeight && fillHeight <= maxHeight) {
      computedHeight = fillHeight;
      objectFit = 'cover';
    } else {
      computedHeight = maxHeight;
      objectFit = 'contain';
    }
  } else {
    computedHeight = viewport === 'mobile' ? 280 : viewport === 'tablet' ? 340 : 420;
    objectFit = 'cover';
  }

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-[#0A0A0A]"
      style={{ height: `${computedHeight}px` }}
      data-testid="hero-section"
      data-hero-viewport={viewport}
    >
      {currentImg && (
        <img
          key={`${viewport}-${currentImg}`}
          src={currentImg}
          alt="Hero"
          onLoad={(e) => {
            const w = e.target.naturalWidth;
            const h = e.target.naturalHeight;
            if (w > 0 && h > 0) setImgRatio(w / h);
          }}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit, objectPosition: 'center center' }}
          data-testid="hero-bg-img"
        />
      )}
      {(showHeading || showSubheading || hasButtons) && (props.heading || props.subheading || hasButtons) && (
        <>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: `rgba(10, 10, 10, ${overlayOpacity / 100})` }} />
          <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 sm:px-8 py-6 h-full">
            {showHeading && props.heading && <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight max-w-4xl drop-shadow-lg" data-testid="hero-heading">{props.heading}</h1>}
            {showSubheading && props.subheading && <p className={`text-sm sm:text-base text-zinc-200 max-w-2xl drop-shadow ${showHeading && props.heading ? 'mt-2 sm:mt-3' : ''}`} data-testid="hero-subheading">{props.subheading}</p>}
            {hasButtons && (
              <div className={`flex flex-wrap gap-3 justify-center ${(showHeading && props.heading) || (showSubheading && props.subheading) ? 'mt-4 sm:mt-6' : ''}`}>
                {showCtaPrimary && props.ctaText && (
                  <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white rounded-sm font-bold uppercase h-11 px-7" onClick={() => navigate(props.ctaLink || '/inventory')} data-testid="hero-cta-primary">{props.ctaText}</Button>
                )}
                {showCtaSecondary && props.ctaSecondaryText && (
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-bold uppercase h-11 px-7" onClick={() => navigate(props.ctaSecondaryLink || '/financing')} data-testid="hero-cta-secondary">{props.ctaSecondaryText}</Button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function PublicStats({ props }) {
  return (
    <section className="bg-[#0A0A0A] py-6" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        {(props.items || []).map((s, i) => (
          <div key={i}>
            <p className="text-2xl md:text-3xl font-bold text-[#E31937]">{s.num}</p>
            <p className="text-xs text-zinc-300 uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PublicFeaturedInventory({ props, navigate, onCallForPrice }) {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    inventoryAPI.list({ limit: 6, featured: true }).then(r => setFeatured(r.data.items || r.data || [])).catch(() => {});
  }, []);
  return (
    <section className="py-16 md:py-24" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0A0A0A] uppercase">{props.heading}</h2>
            {props.subheading && <p className="text-zinc-500 mt-2">{props.subheading}</p>}
          </div>
          <Link to="/inventory" className="text-[#E31937] font-medium text-sm hover:underline flex items-center gap-1">
            {props.linkText || 'View All'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map(item => (
            <Card key={item.id} className="group overflow-hidden rounded-sm border bg-white shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`featured-card-${item.id}`}>
              <div className="relative aspect-video overflow-hidden cursor-pointer" onClick={() => navigate(`/inventory/${item.id}`)}>
                {item.images?.length > 0 ? (
                  <img src={resolveImageUrl(item.images[0])} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-zinc-200 flex flex-col items-center justify-center"><img src={LOGO_URL} alt="CTS" className="w-2/5 opacity-30" /><span className="mt-2 text-sm font-semibold text-zinc-400 uppercase tracking-wider">Coming Soon</span></div>
                )}
                {item.images?.length > 0 && <img src={LOGO_URL} alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2/3 opacity-[0.18] pointer-events-none z-[1] drop-shadow-sm" />}
                <div className="absolute bottom-2 left-2 flex gap-1.5 z-[3]">
                  {item.featured && <span className="bg-[#E31937] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm">Featured</span>}
                  <span className="bg-[#0A0A0A]/70 text-white text-[10px] px-2 py-0.5 rounded-sm">{item.type}</span>
                </div>
                {item.status && item.status !== 'Available' && (
                  <div className="absolute top-0 right-0 overflow-hidden w-32 h-32 pointer-events-none z-[4]">
                    <div className="absolute top-[14px] right-[-34px] w-[160px] text-center text-xs font-extrabold uppercase tracking-widest text-white py-2 rotate-45 shadow-lg bg-[#E31937]" style={{textShadow: '0 1px 3px rgba(0,0,0,0.4)', backgroundImage: 'linear-gradient(to right, #c0102e, #E31937, #ff2d4f, #E31937, #c0102e)'}}>
                      {item.status}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-base text-[#0A0A0A] cursor-pointer hover:text-[#E31937] transition-colors truncate" onClick={() => navigate(`/inventory/${item.id}`)}>
                  {item.year} {item.make} {item.model}
                </h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-zinc-500">
                  {item.type === 'truck' ? (
                    <>
                      {item.engine && <span className="flex items-center gap-1"><Cog className="w-3.5 h-3.5" /><b className="text-zinc-600">Engine:</b> {item.engine}</span>}
                      {item.mileage_km && <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /><b className="text-zinc-600">Mileage:</b> {formatKM(item.mileage_km)}</span>}
                    </>
                  ) : (
                    <span className="capitalize"><b className="text-zinc-600">Condition:</b> {item.condition || 'Used'}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3 pt-2.5 border-t">
                  {item.price ? (
                    <p className="text-lg font-bold text-[#E31937]">{formatPrice(item.price)}</p>
                  ) : (
                    <button className="text-lg font-bold text-[#E31937] hover:underline" onClick={(e) => { e.stopPropagation(); onCallForPrice && onCallForPrice(); }} data-testid={`call-price-featured-${item.id}`}>Call for Price</button>
                  )}
                  <span className="text-xs text-zinc-500 font-medium">Mississauga, ON</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function PublicFeatures({ props }) {
  return (
    <section className="py-16 md:py-24 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#0A0A0A] uppercase mb-10">{props.heading}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(props.items || []).map((f, i) => {
            const Icon = ICONS[f.icon] || Star;
            return (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 bg-[#E31937]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-[#E31937]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0A0A0A]">{f.title}</h3>
                  <p className="text-zinc-500 text-sm mt-1">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PublicTestimonials({ props }) {
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    cmsAPI.get('testimonials').then(r => setTestimonials(r.data?.items || [])).catch(() => {});
  }, []);
  if (testimonials.length === 0) return null;
  return (
    <section className="py-16 md:py-24" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#0A0A0A] uppercase mb-3">{props.heading}</h2>
        {props.subheading && <p className="text-zinc-500 mb-10">{props.subheading}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <Card key={i} className="border border-gray-100 shadow-sm p-6 relative">
              <Quote className="w-8 h-8 text-[#E31937]/15 absolute top-4 right-4" />
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, s) => <Star key={s} className={`w-4 h-4 ${s < (t.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />)}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-5">"{t.text}"</p>
              <div className="border-t border-gray-100 pt-4">
                <p className="font-semibold text-sm text-[#0A0A0A]">{t.name}</p>
                {t.company && <p className="text-xs text-gray-400 mt-0.5">{t.company}</p>}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function PublicCTA({ props, navigate }) {
  return (
    <section className="relative py-20 overflow-hidden">
      {props.backgroundImage && <img src={props.backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-[#0A0A0A]/80" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white uppercase">{props.heading}</h2>
        {props.subheading && <p className="text-zinc-300 mt-3 max-w-lg mx-auto">{props.subheading}</p>}
        {props.ctaText && (
          <Button className="mt-8 bg-[#E31937] hover:bg-[#C4122C] text-white rounded-sm font-bold uppercase h-12 px-8" onClick={() => navigate(props.ctaLink || '/about')}>
            {props.ctaText}
          </Button>
        )}
      </div>
    </section>
  );
}

function PublicTextBlock({ props }) {
  return (
    <section className="py-16 md:py-24">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${props.alignment === 'center' ? 'text-center' : ''}`}>
        <h2 className="text-3xl font-semibold uppercase mb-6">{props.heading}</h2>
        <div className={`space-y-4 text-zinc-600 leading-relaxed ${props.alignment === 'center' ? 'max-w-2xl mx-auto' : ''}`}>
          {(props.content || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    </section>
  );
}

function PublicTextImage({ props }) {
  const isTextLeft = props.layout !== 'text-right';
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className={!isTextLeft ? 'order-2 lg:order-1' : ''}>
            <h2 className="text-3xl font-semibold uppercase mb-6">{props.heading}</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              {(props.content || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <div className={`bg-zinc-50 rounded-sm overflow-hidden min-h-[300px] ${!isTextLeft ? 'order-1 lg:order-2' : ''}`}>
            {props.image ? <img src={props.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-300 text-sm">No image</div>}
          </div>
        </div>
      </div>
    </section>
  );
}

function PublicContactCards({ props }) {
  return (
    <section className="py-12 bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6">
        {(props.items || []).map((c, i) => {
          const Icon = ICONS[c.icon] || MapPin;
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#E31937]/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#E31937]" />
              </div>
              <div>
                <p className="text-sm font-semibold">{c.title}</p>
                <p className="text-zinc-400 text-sm mt-0.5">{c.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PublicSpacer({ props }) {
  return <div style={{ height: props.height || 60 }} />;
}

const RENDERERS = {
  hero: PublicHero,
  stats: PublicStats,
  featured_inventory: PublicFeaturedInventory,
  features: PublicFeatures,
  testimonials: PublicTestimonials,
  cta: PublicCTA,
  text_block: PublicTextBlock,
  text_image: PublicTextImage,
  contact_cards: PublicContactCards,
  spacer: PublicSpacer,
};

export default function PublicPageRenderer({ slug }) {
  const navigate = useNavigate();
  const [sections, setSections] = useState(null);
  const [contactPopup, setContactPopup] = useState(false);

  useEffect(() => {
    // Cache-bust so admin changes show up immediately without a hard refresh
    fetch(`${API_URL}/api/pages/${slug}?t=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(data => setSections(data.sections || []))
      .catch(() => setSections([]));
  }, [slug]);

  if (sections === null) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E31937]" /></div>;
  }

  return (
    <div>
      {sections.map((section, i) => {
        const Comp = RENDERERS[section.type];
        if (!Comp) return null;
        const sectionProps = section.props || {};
        // Stats section is hidden by default unless explicitly enabled by admin
        if (section.type === 'stats' && sectionProps.enabled !== true) return null;
        // Generic visibility flag (admin-controlled hide)
        if (sectionProps.hidden === true) return null;
        return <Comp key={section.id || i} props={sectionProps} navigate={navigate} onCallForPrice={() => setContactPopup(true)} />;
      })}
      <ContactPopup open={contactPopup} onOpenChange={setContactPopup} />
    </div>
  );
}
